/******/ (() => { // webpackBootstrap
class CartQuantity extends HTMLElement {
  constructor() {
    super();
    this.removeButton = this.querySelector('[data-cart-remove-item]');
    this.adjustButtons = this.querySelectorAll('[data-cart-quantity-button]');
    this.input = this.querySelector('[data-cart-quantity-input]');
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleRemoveItem = this.handleRemoveItem.bind(this);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-cart-quantity'});
    this.removeButton?.addEventListener('click', this.handleRemoveItem);
    this.adjustButtons.forEach((button) => button.addEventListener('click', this.handleButtonClick));
    this.input?.addEventListener('change', this.handleInputChange);
    this.updateButtonStates();
  }

  disconnectedCallback() {
    this.removeButton?.removeEventListener('click', this.handleRemoveItem);
    this.adjustButtons.forEach((button) => button.removeEventListener('click', this.handleButtonClick));
    this.input?.removeEventListener('change', this.handleInputChange);
  }

  handleButtonClick(event) {
    event.preventDefault();
    if (this.isLoading || this.isReloading) return;

    const action = event.currentTarget.name;
    const adjustment = action === 'plus' ? this.step : -this.step;
    const previousValue = this.currentQuantity;
    const nextValue = this.normalizeQuantity(previousValue + adjustment);
    this.input.value = nextValue;
    this.updateButtonStates();

    if (nextValue !== previousValue) {
      this.updateCart(nextValue, action);
    }
  }

  handleInputChange(event) {
    event.preventDefault();
    if (this.isLoading || this.isReloading) return;

    const nextValue = this.normalizeQuantity(this.input.value);
    this.input.value = nextValue;
    this.updateButtonStates();
    if (nextValue !== this.previousQuantity) {
      this.updateCart(nextValue, 'input');
    }
  }

  handleRemoveItem(event) {
    event.preventDefault();
    if (this.isLoading || this.isReloading) return;
    this.updateCart(0, 'remove');
  }

  normalizeQuantity(value) {
    const parsedValue = Number.parseInt(value, 10);
    if (Number.isNaN(parsedValue)) return this.minimum;

    const steppedValue = this.minimum + Math.round((parsedValue - this.minimum) / this.step) * this.step;
    return Math.min(Math.max(steppedValue, this.minimum), this.maximum);
  }

  async updateCart(quantity, focusAction) {
    this.setLoadingState(true);
    this.clearErrorMessage();

    let cartChanged = false;
    try {
      let response = await this.updateLineItem(this.itemIndex, quantity);
      cartChanged = true;

      if (this.hasGiftWrap) {
        const adjustment = quantity - this.previousQuantity;
        const giftWrapLine = this.getGiftWrapLine(response);
        const giftWrapQuantity = this.getGiftWrapQuantity(response);
        if (giftWrapLine && giftWrapQuantity !== null) {
          response = await this.updateLineItem(giftWrapLine, Math.max(0, giftWrapQuantity + adjustment));
        }
      }

      // A successful mutation can omit section HTML. Recover the view without
      // repeating the mutation or allowing retries against stale line indexes.
      if (!await this.ensureCartSections(response)) return;

      const announcement = quantity === 0
        ? `${this.productTitle} removed from the cart.`
        : `${this.productTitle} quantity ${quantity}. ${this.subtotalLabel} ${this.getCartSubtotal(response)}.`;
      const reachedLimit = (focusAction === 'minus' && quantity <= this.minimum)
        || (focusAction === 'plus' && quantity >= this.maximum);
      const restoreAction = reachedLimit ? 'input' : focusAction;

      window.eventBus.emit('update:cart:drawer', {
        ...response,
        cartFocusKey: this.focusKey(restoreAction),
        cartFocusAction: restoreAction,
        cartAnnouncement: announcement,
      });
      this.cartPage?.renderCartPage(response, {
        focusKey: this.focusKey(restoreAction),
        focusAction: restoreAction,
        announcement,
      });
      window.wetheme.updateCartCount(response);
    } catch (error) {
      if (cartChanged) {
        // A later rendering/gift-wrap error must not offer a stale-line retry.
        this.isReloading = true;
        window.location.reload();
      } else {
        if (this.input) this.input.value = this.previousQuantity;
        this.updateButtonStates();
        this.renderErrorMessage(error?.cartMessage || this.genericErrorMessage);
      }
    } finally {
      // Successful replacement normally detaches this element; missing markup
      // or a rendering exception must not strand a still-connected control.
      this.setLoadingState(false);
    }
  }

  async ensureCartSections(cart) {
    const required = [['cart-drawer', [
      '[data-cart-dynamic-content="cart-header"]',
      '[data-cart-dynamic-content="cart-body"]',
      '[data-cart-dynamic-content="cart-footer"]',
    ]]];
    if (this.cartPage) required.push([this.sectionId, ['[data-cart-page-content]']]);
    const complete = (sections) => required.every(([id, selectors]) => {
      const html = sections?.[id];
      if (typeof html !== 'string' || !html.trim()) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      return selectors.every((selector) => parsed.querySelector(selector));
    });
    if (complete(cart.sections)) return true;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const ids = required.map(([id]) => id).join(',');
      const response = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids)}`, {
        method: 'GET', cache: 'no-store', signal: controller.signal,
      });
      if (response.ok) {
        const sections = await response.json();
        if (complete(sections)) {
          cart.sections = sections;
          return true;
        }
      }
    } catch (error) {
      // The mutation may already have succeeded. Never replay it as a retry.
    } finally {
      clearTimeout(timeout);
    }
    this.isReloading = true;
    window.location.reload();
    return false;
  }

  async updateLineItem(line, quantity) {
    const sectionIds = ['cart-drawer'];
    if (this.sectionId) sectionIds.push(this.sectionId);

    const response = await fetch(window.routes.cart_change_url, {
      ...this.fetchConfig(),
      body: JSON.stringify({
        line,
        quantity,
        sections: sectionIds,
        sections_url: window.location.pathname,
      }),
    });

    const responseText = await response.text();
    let parsedResponse = {};
    try {
      parsedResponse = responseText ? JSON.parse(responseText) : {};
    } catch (error) {
      throw new Error(this.genericErrorMessage);
    }

    if (!response.ok || parsedResponse.errors || parsedResponse.status >= 400) {
      const error = new Error(this.genericErrorMessage);
      // Keep inventory/validation guidance, never surface transport exceptions.
      if (response.status === 422 || parsedResponse.status === 422
        || (response.ok && parsedResponse.errors && !(parsedResponse.status >= 400))) {
        error.cartMessage = this.normalizeError(parsedResponse.errors || parsedResponse.description || parsedResponse.message);
      }
      throw error;
    }

    return parsedResponse;
  }

  fetchConfig() {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    };
  }

  normalizeError(error) {
    if (typeof error === 'string' && error.trim()) return error;
    if (error?.message) return error.message;
    if (error && typeof error === 'object') {
      const message = Object.values(error).flat().filter(Boolean).join(' ');
      if (message) return message;
    }
    return this.genericErrorMessage;
  }

  renderErrorMessage(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.removeAttribute('hidden');
    this.errorElement.setAttribute('aria-hidden', 'false');
    if (this.errorElement.dataset.cartIsLastItem === 'true') {
      this.errorElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  clearErrorMessage() {
    if (!this.errorElement) return;
    this.errorElement.textContent = '';
    this.errorElement.setAttribute('hidden', '');
    this.errorElement.setAttribute('aria-hidden', 'true');
  }

  updateButtonStates() {
    if (!this.input) return;
    const quantity = this.currentQuantity;
    const minusButton = this.querySelector('[data-cart-quantity-button][name="minus"]');
    const plusButton = this.querySelector('[data-cart-quantity-button][name="plus"]');
    if (minusButton) {
      minusButton.disabled = quantity <= this.minimum;
      minusButton.setAttribute('aria-disabled', this.isLoading || minusButton.disabled ? 'true' : 'false');
    }
    if (plusButton) {
      plusButton.disabled = quantity >= this.maximum;
      plusButton.setAttribute('aria-disabled', this.isLoading || plusButton.disabled ? 'true' : 'false');
    }
  }

  setLoadingState(isLoading) {
    this.toggleAttribute('data-loading', isLoading);
    this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    if (this.input) this.input.readOnly = isLoading;
    if (this.removeButton) this.removeButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
    this.updateButtonStates();
  }

  getCartSubtotal(cart) {
    const drawerHtml = cart.sections?.['cart-drawer'];
    if (drawerHtml) {
      const subtotal = new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('[data-cart-subtotal]');
      if (subtotal?.textContent.trim()) return subtotal.textContent.trim();
    }
    return this.updatedSubtotal || '';
  }

  getGiftWrapLine(cart) {
    const index = cart.items?.findIndex((item) => item.variant_id === this.giftWrapVariantId);
    return index >= 0 ? index + 1 : null;
  }

  getGiftWrapQuantity(cart) {
    return cart.items?.find((item) => item.variant_id === this.giftWrapVariantId)?.quantity ?? null;
  }

  focusKey(action) {
    return `${this.itemKey}-${action}`;
  }

  get cartRoot() {
    return this.closest('cart-drawer, cart-page');
  }

  get cartPage() {
    return this.closest('cart-page');
  }

  get errorElement() {
    return [...(this.cartRoot?.querySelectorAll('[data-cart-error-key]') || [])]
      .find((element) => element.dataset.cartErrorKey === this.itemKey);
  }

  get itemIndex() {
    return Number.parseInt(this.dataset.itemIndex, 10);
  }

  get itemKey() {
    return this.dataset.itemKey || String(this.itemIndex);
  }

  get productTitle() {
    return this.dataset.productTitle || 'Item';
  }

  get sectionId() {
    return this.dataset.sectionId || '';
  }

  get subtotalLabel() {
    return this.dataset.subtotalLabel || 'Subtotal';
  }

  get updatedSubtotal() {
    return this.dataset.updatedSubtotal || '';
  }

  get genericErrorMessage() {
    return this.dataset.errorMessage || 'The cart could not be updated. Please try again.';
  }

  get previousQuantity() {
    return Number.parseInt(this.dataset.itemQuantity, 10) || this.minimum;
  }

  get currentQuantity() {
    return Number.parseInt(this.input?.value, 10) || this.minimum;
  }

  get minimum() {
    return Number.parseInt(this.input?.min, 10) || 1;
  }

  get maximum() {
    const maximum = Number.parseInt(this.input?.max, 10);
    return Number.isNaN(maximum) ? Number.MAX_SAFE_INTEGER : maximum;
  }

  get step() {
    return Number.parseInt(this.input?.step, 10) || 1;
  }

  get hasGiftWrap() {
    return this.dataset.hasGiftWrap === 'true';
  }

  get giftWrapVariantId() {
    return Number.parseInt(this.dataset.giftWrapVariantId, 10);
  }

  get isLoading() {
    return this.hasAttribute('data-loading');
  }
}

customElements.define('cart-quantity', CartQuantity);
/******/ })()
;
